// The River player: joins the endless song at the current world bar and keeps the engine fed.
// The engine schedules every note itself from its bar ↔ sample anchor; this class only
//  - syncs to the server clock (NTP-style, re-checked every 60 s),
//  - computes the anchor so bar starts are *audible* at epoch + n × bar length (1.92 s at
//    125 BPM, 3.33 s on the 72 BPM site; output latency included),
//  - sends periods (today + tomorrow near the dawn bar) and the loops of upcoming slots,
//  - corrects drift with sample nudges at bar boundaries (never jumping mid-bar).
import { DEMO } from "../demo/flag";
import { clock, STREAM_BPM } from "../stream";
import {
  dayAt,
  DEFAULT_EPOCH_MS,
  driftCorrection,
  engineSampleForWorldTime,
  measureOffset,
  msUntilNextDay,
  OffsetTracker,
  periodSpec,
  type Bpm,
  type Lineup,
  type LoopData,
  type PeriodDef,
  type PeriodSpec,
} from "@eb/shared";
import type { EngineHost } from "../audio/host";
import type { LineupSource } from "./source";

export interface ServerTime {
  now: number;
  epochMs: number;
}

export async function fetchServerTime(): Promise<ServerTime> {
  // The static demo has no server: the device clock is the world clock.
  if (DEMO) return { now: Date.now(), epochMs: DEFAULT_EPOCH_MS };
  const r = await fetch("/api/time", { cache: "no-store" });
  if (!r.ok) throw new Error(`time: HTTP ${r.status}`);
  return (await r.json()) as ServerTime;
}

/** Slots ahead of the playhead whose loops are kept in the engine. */
const LOOKAHEAD_SLOTS = 3;
const RESYNC_MS = 60_000;

/** `waiting`: the stream should start, but the browser needs a tap or key press first. */
export type PlayerStatus = "idle" | "waiting" | "joining" | "playing" | "error";

export class RiverPlayer {
  status: PlayerStatus = "idle";
  private joinAttempt = 0;
  error = "";
  /** Local→world clock offset (ms), before the first sync: 0. */
  offsetMs = 0;
  syncErrorBoundMs = 0;
  lastNudge = 0;
  nudges = 0;
  relocations = 0;
  private tracker = new OffsetTracker();
  /** Periods and specs by "bpm:day" (tapes and profiles also play songs of the other stream). */
  private periods = new Map<string, Promise<PeriodDef>>();
  private specs = new Map<string, PeriodSpec>();
  private lineups = new Map<number, Promise<Lineup>>();
  private loops = new Map<string, Promise<LoopData | undefined>>();
  private sentPeriods = new Set<number>();
  private sentLoops = new Set<string>();
  private timer: ReturnType<typeof setInterval> | null = null;
  private syncTimer: ReturnType<typeof setInterval> | null = null;
  private listeners = new Set<() => void>();

  constructor(
    readonly source: LineupSource,
    private host: EngineHost | null = null,
  ) {}

  /** Replays shift the song's clock into the past (0 = live). */
  shiftMs = 0;

  /** World time now (epoch ms, fractional), from the synced clock (shifted for replays). */
  worldNow(): number {
    return performance.timeOrigin + performance.now() + this.offsetMs + this.shiftMs;
  }

  epochMs(): number {
    return this.source.epochMs();
  }

  onChange(l: () => void): () => void {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  }

  private emit() {
    for (const l of this.listeners) l();
  }

  /** Sync the clock without audio (the River animates before "Join"). */
  async syncClock(): Promise<void> {
    const est = await measureOffset(
      async () => (await fetchServerTime()).now,
      () => performance.timeOrigin + performance.now(),
    );
    this.offsetMs = this.tracker.update(est);
    this.syncErrorBoundMs = est.errorBoundMs;
    this.emit();
  }

  period(day: number, bpm: Bpm = STREAM_BPM): Promise<PeriodDef> {
    const key = `${bpm}:${day}`;
    let p = this.periods.get(key);
    if (!p) {
      p = this.source.period(day, bpm);
      this.periods.set(key, p);
      p.catch(() => this.periods.delete(key));
    }
    return p;
  }

  lineup(day: number): Promise<Lineup> {
    let p = this.lineups.get(day);
    if (!p) {
      p = this.source.lineup(day);
      this.lineups.set(day, p);
      p.catch(() => this.lineups.delete(day));
    }
    return p;
  }

  /** Bumps whenever a cached lineup changes (live patches), so views refresh. */
  lineupGeneration = 0;

  /** Re-fetch the cached lineups of today and later (after a (re)connect, patches may have been
   *  missed); keeps the newer version. */
  async refreshLineups(): Promise<void> {
    const today = dayAt(this.worldNow(), this.epochMs());
    for (const [day, p] of [...this.lineups]) {
      if (day < today) continue;
      const old = await p.catch(() => null);
      const fresh = await this.source.lineup(day).catch(() => null);
      if (fresh && (!old || fresh.version > old.version)) this.setLineup(fresh);
    }
  }

  /** Replace a cached lineup (live patches). */
  setLineup(l: Lineup): void {
    this.lineups.set(l.day, Promise.resolve(l));
    this.lineupGeneration++;
    this.emit();
  }

  loop(id: string): Promise<LoopData | undefined> {
    let p = this.loops.get(id);
    if (!p) {
      p = this.source.loops([id]).then((m) => m.get(id));
      this.loops.set(id, p);
      p.catch(() => this.loops.delete(id));
    }
    return p;
  }

  /** Prefetch loop data for a batch of ids in one request. */
  prefetch(ids: string[]): void {
    const missing = ids.filter((id) => !this.loops.has(id));
    if (!missing.length) return;
    const batch = this.source.loops(missing);
    for (const id of missing) {
      const p = batch.then((m) => m.get(id));
      this.loops.set(id, p);
      p.catch(() => this.loops.delete(id));
    }
  }

  /** Start audio at the current bar (call from a user gesture after `host.resume()`). */
  async join(host: EngineHost): Promise<void> {
    if (this.status === "joining" || this.status === "playing") return;
    this.host = host;
    this.status = "joining";
    const attempt = ++this.joinAttempt;
    // Stopped (Leave, or the editor opened) while joining: don't start after all.
    const cancelled = () => attempt !== this.joinAttempt || this.status !== "joining";
    this.emit();
    try {
      await host.resume();
      await this.syncClock();
      const epoch = this.epochMs();
      const now = this.worldNow();
      const day = dayAt(now, epoch);
      await this.ensurePeriod(day);
      if (msUntilNextDay(now, epoch) < 5 * 60_000) await this.ensurePeriod(day + 1);
      if (cancelled()) return;
      await this.feed();
      if (cancelled()) return;
      this.locate();
      this.status = "playing";
      this.timer = setInterval(() => void this.feed().catch((e) => this.fail(e)), 500);
      this.syncTimer = setInterval(() => void this.resync().catch(() => undefined), RESYNC_MS);
      // A first drift check once the output latency has settled.
      setTimeout(() => void this.correctDrift(), 2500);
      this.emit();
    } catch (e) {
      if (!cancelled()) this.fail(e);
    }
  }

  /** Mark that the stream will start on the next tap (see `autoJoin`). */
  setWaiting(on: boolean): void {
    if (on ? this.status !== "idle" : this.status !== "waiting") return;
    this.status = on ? "waiting" : "idle";
    this.emit();
  }

  stop(): void {
    this.joinAttempt++;
    if (this.timer) clearInterval(this.timer);
    if (this.syncTimer) clearInterval(this.syncTimer);
    this.timer = this.syncTimer = null;
    this.host?.worldStop();
    this.sentPeriods.clear();
    this.sentLoops.clear();
    this.status = "idle";
    this.emit();
  }

  private fail(e: unknown) {
    this.status = "error";
    this.error = e instanceof Error ? e.message : String(e);
    this.emit();
  }

  /** The engine spec of a day of a stream (loads its kit into the worklet). */
  async spec(day: number, host: EngineHost, bpm: Bpm = STREAM_BPM): Promise<PeriodSpec> {
    const def = await this.period(day, bpm);
    const key = `${bpm}:${day}`;
    let spec = this.specs.get(key);
    if (!spec) {
      const manifest = await host.loadKit(def.kit);
      spec = periodSpec(def, manifest);
      this.specs.set(key, spec);
    }
    return spec;
  }

  /** Solo playback of one song outside the world clock (pauses the River), at its stream's tempo. */
  async preview(day: number, loop: LoopData, host?: EngineHost, bpm: Bpm = STREAM_BPM): Promise<void> {
    const h: EngineHost = host ?? this.host ?? (await import("../audio/host").then((m) => m.engineHost()));
    this.host ??= h;
    await h.resume();
    this.previewResume = this.status === "playing";
    if (this.previewResume) h.worldStop();
    const spec = await this.spec(day, h, bpm);
    h.setEditor(spec, loop, { type: "song" });
    h.play();
    this.previewing = true;
    this.emit();
  }

  /** End a preview; rejoin the song if it was playing. */
  async endPreview(): Promise<void> {
    if (!this.previewing || !this.host) return;
    this.host.stop();
    this.previewing = false;
    if (this.previewResume) this.locate();
    this.emit();
  }

  previewing = false;
  private previewResume = false;

  private async ensurePeriod(day: number): Promise<PeriodSpec> {
    const host = this.host!;
    const spec = await this.spec(day, host);
    if (!this.sentPeriods.has(day)) {
      host.setPeriod(spec);
      this.sentPeriods.add(day);
      for (const d of this.sentPeriods) if (Math.abs(d - day) > 1) this.sentPeriods.delete(d);
    }
    return spec;
  }

  /** Send loops for the current and next few slots (and tomorrow's sounds before the dawn bar). */
  private async feed(): Promise<void> {
    const host = this.host;
    if (!host) return;
    const epoch = this.epochMs();
    const now = this.worldNow();
    const bar = clock.barAt(now, epoch);
    const c = clock.barContent(bar);
    let day = c.day;
    let slot = c.kind === "dawn" ? clock.SLOTS_PER_DAY : c.slot;
    if (msUntilNextDay(now, epoch) < 5 * 60_000) await this.ensurePeriod(day + 1);
    for (let k = 0; k <= LOOKAHEAD_SLOTS; k++) {
      if (slot >= clock.SLOTS_PER_DAY) {
        day += 1;
        slot = 0;
      }
      await this.ensurePeriod(day);
      const lineup = await this.lineup(day);
      const entry = lineup.entries[slot];
      const key = `${day}:${slot}:${entry.loopId}:${lineup.version}`;
      if (!this.sentLoops.has(key)) {
        const data = await this.loop(entry.loopId);
        if (data) {
          host.setLoop(day, slot, data, entry.gain);
          this.sentLoops.add(key);
        }
      }
      slot++;
    }
    if (this.sentLoops.size > 64) this.sentLoops = new Set([...this.sentLoops].slice(-32));
  }

  /** The engine sample at which world bar `bar` must start to be heard on time. */
  private wantBarStart(bar: number): number {
    const host = this.host!;
    const audible = host.audibleNow();
    return engineSampleForWorldTime(
      clock.barStartMs(bar, this.epochMs()),
      audible,
      (p) => performance.timeOrigin + p + this.offsetMs,
      host.sampleRate,
    );
  }

  private locate(): void {
    const host = this.host!;
    const bar = clock.barAt(this.worldNow(), this.epochMs());
    host.locate(bar, Math.round(this.wantBarStart(bar)), STREAM_BPM);
  }

  private async resync(): Promise<void> {
    await this.syncClock();
    this.correctDrift();
  }

  /** How far (ms) the engine's current bar start is from where world time says it should be
   *  audible; null when not playing. Positive: the engine is early. */
  audibleErrorMs(): number | null {
    const host = this.host;
    const c = host?.lastClock;
    if (!host || !c || !c.world || this.status !== "playing") return null;
    return ((this.wantBarStart(c.worldBar) - c.worldBarStart) / host.sampleRate) * 1000;
  }

  /** Compare the engine's bar start with where it should be; nudge (or relocate if way off). */
  correctDrift(): void {
    if (this.previewing) return;
    const host = this.host;
    const c = host?.lastClock;
    if (!host || !c || !c.world || this.status !== "playing") return;
    const want = this.wantBarStart(c.worldBar);
    const fix = driftCorrection(want, c.worldBarStart, host.sampleRate);
    if (fix.relocate) {
      this.relocations++;
      this.locate();
    } else if (fix.nudge !== 0) {
      this.nudges++;
      this.lastNudge = fix.nudge;
      host.nudge(fix.nudge);
    }
    this.emit();
  }

  /** First bar of a slot (helpers for the UI). */
  static slotStartBar = clock.slotStartBar;
}
